import { h } from 'preact';
import { isPowsterEmployee } from 'utils/roles';

import MovieEditor from 'components/editor/movies/movieEditor';
import MediaAsset from 'components/editor/movies/movieMedia/mediaAsset';
import MediaAssetGroup from 'components/editor/movies/movieMedia/mediaAssetGroup';
import SimpleField from 'components/editor/settings/simpleField';

import s from 'components/editor/movies/movieMedia/movieMedia.sss';

const AssetSection = ({ label, note, actions, children }) => (
	<div class={s.section}>
		<div class={s.headerRow}>
			<h4>{label}</h4>
			{actions}
		</div>
		{note && <span class={s.note}>{note}</span>}
		<div class={s.assetsRow}>{children}</div>
	</div>
);

export default class MovieMediaEditor extends MovieEditor {
	s = s;
	
	get title() {
		return 'Media';
	}

	get movieTitle() {
		return this.movie?.title || 'Movie';
	}

	constructor(props) {
		super(props);
	}

	renderOnesheet(opts = {}) {
		return (
			<MediaAsset
				{...this.props}
				path="/content/onesheet"
				label="Onesheet"
				assetData={{ type: 'Onesheet', aspectRatio: '350:517', recommendedDimensions: '1400:2068' }}
				altPlaceholder={`${this.movieTitle} Poster`}
				helpTip="Required"
				key="onesheet"
				{...opts}
			/>
		);
	}

	renderHeaderImage(opts = {}) {
		return (
			<MediaAsset
				{...this.props}
				path="/content/headerImage"
				label="Header image (Mobile)"
				assetData={{ type: 'Header', aspectRatio: '4:3', recommendedDimensions: '1400:1050' }}
				key="header-image"
				helpTip="Required for showtimes"
				hideAlt
				{...opts}
			/>
		);
	}

	renderTitleTreatment(opts = {}) {
		return (
			<MediaAsset
				{...this.props}
				path="/content/pages/home/tt"
				label="Title treatment"
				assetData={{ type: 'Promo Tout Image', recommendedDimensions: '800:500' }}
				altPlaceholder={`TT: ${this.movieTitle}`}
				helpTip="Required for movie pages"
				key="title-treatment"
				{...opts}
			/>
		);
	}

	renderHeroVideo(opts = {}) {
		return (
			<MediaAsset
				{...this.props}
				path="/content/hero/video"
				label="Hero video"
				assetData={{ type: 'Hero Video Desktop', aspectRatio: '16:9', recommendedDimensions: '1920:1080' }}
				altPlaceholder={`${this.movieTitle} Hero Video`}
				key="hero-video"
				isVideo
				hideAlt
				{...opts}
			/>
		);
	}

	renderHeroImage(opts = {}) {
		return (
			<MediaAsset
				{...this.props}
				path="/content/hero/still"
				label="Hero still"
				assetData={{ type: 'Hero Still Desktop', aspectRatio: '16:9', recommendedDimensions: '1920:1080' }}
				altPlaceholder={`${this.movieTitle} Hero Still`}
				helpTip="Required"
				key="hero-still"
				{...opts}
			/>
		);
	}

	renderTrailers(opts = {}) {
		return (
			<MediaAssetGroup
				{...this.props}
				label="YouTube trailer"
				path="/content/trailers"
				key="trailers"
				isVideo
				sortable
				youtube
				{...opts}
			/>
		);
	}

	renderShareImage(opts = {}) {
		return (
			<MediaAsset
				{...this.props}
				path="/content/metaData/shareImage"
				label="Share image"
				assetData={{ type: 'Share Image', aspectRatio: '2:1', recommendedDimensions: '1920:960' }}
				helpTip="Required"
				key="share-image"
				hideAlt
				{...opts}
			/>
		);
	}

	renderFavicon(opts = {}) {
		return (
			<MediaAsset
				{...this.props}
				path="/content/metaData/favicon"
				label="Favicon"
				assetData={{ type: 'Favicon', aspectRatio: '1:1', recommendedDimensions: '512:512' }}
				key="favicon"
				hideAlt
				{...opts}
			/>
		);
	}

	renderGallery(opts = {}) {
		return (
			<MediaAssetGroup
				{...this.props}
				label="Gallery image"
				path="/content/pages/gallery/images"
				assetData={{ type: 'Gallery Image' }}
				assetLabel={(asset) => asset?.caption || `Image ${asset?.order !== undefined ? asset.order + 1 : ''}`}
				altPlaceholder={(asset) => `${this.movieTitle} Gallery Image ${asset?.order !== undefined ? asset.order + 1 : ''}`}
				extraSettings={(props) => (<SimpleField dark label="Image caption (optional)" path={`${props.dataRoot}${props.path}/caption`} />)}
				key="gallery"
				sortable
				{...opts}
			/>
		);
	}

	renderFooterLogos(opts = {}) {
		return (
			<MediaAssetGroup
				{...this.props}
				label="Footer logos"
				path="/content/footer/logos"
				assetData={{ type: 'Footer Logo' }}
				assetLabel={(asset) => asset?.alt || `Footer logo ${asset?.order !== undefined ? asset.order + 1 : ''}`}
				key="footer-logos"
				sortable
				{...opts}
			/>
		);
	}

	renderMiscExtraSettings(asset) {
		return [
			<SimpleField dark label="Tag (optional)" path={`${asset.dataRoot}${asset.path}/tag`} inheritPath={`${asset.dataRoot}/base_movie${asset.path}/tag`} />,
			<SimpleField dark readonly copyable label="Asset URL" path={`${asset.dataRoot}${asset.path}/url`} inheritPath={`${asset.dataRoot}/base_movie${asset.path}/url`} />,
		];
	}

	renderMiscAssets(opts = {}) {
		return (
			<MediaAssetGroup
				{...this.props}
				label="Miscellaneous assets"
				path="/content/miscAssets"
				assetData={{ type: 'Miscellaneous' }}
				assetLabel={(asset) => asset?.tag || `Asset ${asset?.order !== undefined ? asset.order + 1 : ''}`}
				extraSettings={this.renderMiscExtraSettings}
				key="misc-assets"
				hideAdd={!isPowsterEmployee()}
				hideDelete={!isPowsterEmployee()}
				hideAlt
				anyType
				sortable
				{...opts}
			/>
		);
	}

	/**
	 * Override this method to provide the sections
	 * Sections should be an array of objects with the shape { label: string, note?: string, content: preact component }
	 */
	getSections() {}

	renderDescription() {
		return [
			'Controls all media related to the movie.',
			'Changes here will be reflected on both the hubsite and movie pages, unless overridden in the page editor.'
		].map(str => <p>{str}</p>);
	}

	renderHeader() {
		const description = this.renderDescription();

		return (
			<div class={this.s.header}>
				<h2>{this.title || 'Media'}</h2>
				{description && (<div class={this.s.description}>{description}</div>)}
			</div>
		);
	}

	renderVisual() {
		const sections = this.getSections() || [];
		return (
			<div class={this.s.visual} key="movie-media">
				{this.renderHeader()}
				{sections.map(({ label, note, actions, content }) => (
					<AssetSection label={label} note={note} actions={actions}>
						{content}
					</AssetSection>
				))}
			</div>
		);
	}

	// This should be kept update to date with the list of media assets available to render (functions above)
	getSettings() {
		return [
			this.renderOnesheet(),
			this.renderHeaderImage(),
			this.renderTitleTreatment(),
			this.renderHeroVideo(),
			this.renderHeroImage(),
			this.renderTrailers(),
			this.renderShareImage(),
			this.renderFavicon(),
			this.renderGallery(),
			this.renderFooterLogos(),
			this.renderMiscAssets(),
		].filter(Boolean);
	}

	renderSettings() {
		return (
			<div>
				{this.getSettings()}
			</div>
		);
	}
}
